import { motion } from 'framer-motion';
import { ArrowRight, Globe, ShieldCheck, Zap } from 'lucide-react';
import HeroBackground from './HeroBackground';

const FEATURES = [
  {
    icon: ShieldCheck,
    title: 'Compliance First',
    description: 'Built-in risk management for Finance & HIPAA-ready protocols for Healthcare.',
    bgColor: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
  },
  {
    icon: Zap,
    title: 'Complex Workflows',
    description:
      'Handling branching logic for loans, multi-step booking, and dynamic troubleshooting.',
    bgColor: 'bg-amber-100',
    iconColor: 'text-amber-600',
  },
  {
    icon: Globe,
    title: 'Scale Ready',
    description:
      'Used by major e-commerce platforms and legal firms to automate customer service at scale.',
    bgColor: 'bg-blue-100',
    iconColor: 'text-blue-600',
  },
] as const;

const Hero: React.FC = () => {
  return (
    <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden">
      <HeroBackground />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-block py-1 px-3 rounded-full bg-brand-50 text-brand-600 text-sm font-semibold mb-6 border border-brand-100">
            Enterprise-Grade Process Automation
          </span>
          <h1 className="text-4xl md:text-6xl font-extrabold text-slate-900 tracking-tight mb-6 leading-tight">
            Complex Business Logic,
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-600 to-indigo-600">
              Simplified & Secured.
            </span>
          </h1>
          <p className="mt-4 max-w-3xl mx-auto text-xl text-slate-600 mb-10 leading-relaxed">
            <span className="block text-slate-900 font-bold text-2xl mb-3">
              "When using LLMs, we don't hope for results—we ensure them."
            </span>
            We guarantee outcomes by enforcing behavioral rules through structural mechanisms,
            turning probabilistic AI into reliable business infrastructure.
          </p>

          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <button
              type="button"
              className="group flex items-center justify-center px-8 py-4 border border-transparent text-lg font-medium rounded-xl text-white bg-brand-600 hover:bg-brand-700 shadow-lg hover:shadow-xl hover:shadow-brand-500/30 transition-all duration-200 transform hover:-translate-y-1 cursor-pointer"
            >
              Explore Services
              <ArrowRight
                className="ml-2 h-5 w-5 transition-transform duration-200 group-hover:translate-x-1"
                aria-hidden="true"
              />
            </button>
            <button
              type="button"
              className="flex items-center justify-center px-8 py-4 border border-slate-200 text-lg font-medium rounded-xl text-slate-700 bg-white hover:bg-slate-50 hover:border-slate-300 hover:text-brand-600 shadow-sm hover:shadow-md transition-all duration-200 transform hover:-translate-y-1 cursor-pointer"
            >
              View Documentation
            </button>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-8 text-left"
        >
          {FEATURES.map((feature) => (
            <div
              key={feature.title}
              className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100"
            >
              <div
                className={`w-12 h-12 ${feature.bgColor} rounded-lg flex items-center justify-center mb-4`}
              >
                <feature.icon className={`${feature.iconColor} h-6 w-6`} aria-hidden="true" />
              </div>
              <h3 className="font-bold text-lg text-slate-900 mb-2">{feature.title}</h3>
              <p className="text-slate-600">{feature.description}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
